import React, { useState } from 'react';
import { ArrowLeftIcon, UploadIcon, XIcon, PlusIcon, EyeIcon, SaveIcon, StarIcon } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
export const AppStoreListing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialData = location.state || {};
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [formData, setFormData] = useState({
    title: initialData.title || '',
    slug: initialData.slug || '',
    description: initialData.description || '',
    icon: initialData.icon || 'sparkles',
    iconColor: initialData.iconColor || 'bg-gradient-to-br from-indigo-500 to-purple-600',
    categories: initialData.categories || [],
    author: 'Your Name',
    createdBy: 'Your Company',
    aisUsed: initialData.aiModel ? [initialData.aiModel] : [],
    price: 'free',
    screenshots: [] as string[],
    additionalDescription: '',
    features: [''],
    version: '1.0.0',
    allowImageUpload: initialData.allowImageUpload || false,
    allowFileUpload: initialData.allowFileUpload || false,
    fileTypes: initialData.fileTypes || [],
    enableVoiceInput: initialData.enableVoiceInput || false,
    maxFileSize: initialData.maxFileSize || '5',
    enableCodeExecution: initialData.enableCodeExecution || false
  });
  const priceOptions = [{
    value: 'free',
    label: 'Free'
  }, {
    value: '0.99',
    label: '$0.99'
  }, {
    value: '1.99',
    label: '$1.99'
  }, {
    value: '2.99',
    label: '$2.99'
  }, {
    value: '4.99',
    label: '$4.99'
  }, {
    value: '9.99',
    label: '$9.99'
  }, {
    value: '14.99',
    label: '$14.99'
  }, {
    value: '19.99',
    label: '$19.99'
  }, {
    value: '29.99',
    label: '$29.99'
  }, {
    value: '49.99',
    label: '$49.99'
  }];
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const newScreenshots = Array.from(files).map(file => URL.createObjectURL(file));
      setFormData({
        ...formData,
        screenshots: [...formData.screenshots, ...newScreenshots]
      });
    }
  };
  const removeScreenshot = (index: number) => {
    setFormData({
      ...formData,
      screenshots: formData.screenshots.filter((_, i) => i !== index)
    });
  };
  const addFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, '']
    });
  };
  const updateFeature = (index: number, value: string) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({
      ...formData,
      features: newFeatures
    });
  };
  const removeFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };
  const displayPrice = formData.price === 'free' ? 'Free' : `$${formData.price}`;
  if (isPreviewMode) {
    return <div className="bg-gray-50 min-h-screen" data-id="element-1158">
        {/* Preview Mode Header */}
        <div className="bg-white border-b border-gray-200 py-4" data-id="element-1159">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 flex justify-between items-center" data-id="element-1160">
            <button onClick={() => setIsPreviewMode(false)} className="flex items-center text-gray-600 hover:text-indigo-600" data-id="element-1161">
              <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-1162" />
              <span className="text-sm" data-id="element-1163">Back to Editing</span>
            </button>
            <span className="text-sm text-gray-500" data-id="element-1164">Preview Mode</span>
          </div>
        </div>
        {/* App Hero Section */}
        <div className="bg-white border-b border-gray-200" data-id="element-1165">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6" data-id="element-1166">
            <div className="flex flex-col sm:flex-row sm:items-start sm:space-x-8" data-id="element-1167">
              <div className={`w-24 h-24 sm:w-32 sm:h-32 ${formData.iconColor} rounded-2xl flex items-center justify-center mx-auto sm:mx-0 mb-4 sm:mb-0`} data-id="element-1168">
                <span className="text-white text-4xl" data-id="element-1169">📱</span>
              </div>
              <div className="flex-1 text-center sm:text-left" data-id="element-1170">
                <h1 className="text-xl sm:text-2xl font-bold mb-1" data-id="element-1171">
                  {formData.title}
                </h1>
                <p className="text-gray-500 text-sm sm:text-base" data-id="element-1172">
                  By {formData.author}
                </p>
                <div className="flex flex-wrap justify-center sm:justify-start items-center mt-4 gap-4 text-sm" data-id="element-1173">
                  <div className="flex items-center" data-id="element-1174">
                    <StarIcon className="h-5 w-5 text-amber-400 fill-amber-400" data-id="element-1175" />
                    <span className="ml-1 font-medium" data-id="element-1176">New</span>
                  </div>
                  <span className="hidden sm:block text-gray-500" data-id="element-1177">|</span>
                  <span className="text-gray-500" data-id="element-1178">{displayPrice}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* App Content */}
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 sm:py-8" data-id="element-1179">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-1180">
            <div className="lg:col-span-2" data-id="element-1181">
              {/* Screenshots */}
              {formData.screenshots.length > 0 && <section className="bg-white rounded-lg p-4 sm:p-6 mb-6" data-id="element-1182">
                  <h2 className="text-lg font-medium mb-4" data-id="element-1183">Screenshots</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4" data-id="element-1184">
                    {formData.screenshots.map((screenshot, index) => <div key={index} className="aspect-video bg-gray-100 rounded-lg overflow-hidden" data-id="element-1185">
                        <img src={screenshot} alt={`Screenshot ${index + 1}`} className="w-full h-full object-cover" data-id="element-1186" />
                      </div>)}
                  </div>
                </section>}
              {/* Description */}
              <section className="bg-white rounded-lg p-4 sm:p-6 mb-6" data-id="element-1187">
                <h2 className="text-lg font-medium mb-4" data-id="element-1188">Description</h2>
                <p className="text-gray-600 text-sm sm:text-base whitespace-pre-wrap" data-id="element-1189">
                  {formData.description}
                </p>
                {formData.additionalDescription && <p className="text-gray-600 text-sm sm:text-base mt-4 whitespace-pre-wrap" data-id="element-1190">
                    {formData.additionalDescription}
                  </p>}
              </section>
              {/* Features */}
              {formData.features.some(f => f.trim()) && <section className="bg-white rounded-lg p-4 sm:p-6" data-id="element-1191">
                  <h2 className="text-lg font-medium mb-4" data-id="element-1192">Key Features</h2>
                  <ul className="space-y-2" data-id="element-1193">
                    {formData.features.filter(f => f.trim()).map((feature, index) => <li key={index} className="flex items-start text-gray-600" data-id="element-1194">
                          <span className="text-indigo-600 mr-2" data-id="element-1195">•</span>
                          {feature}
                        </li>)}
                  </ul>
                </section>}
            </div>
            {/* Sidebar */}
            <div data-id="element-1196">
              <div className="bg-white rounded-lg p-4 sm:p-6 sticky top-4" data-id="element-1197">
                <h3 className="font-medium mb-4" data-id="element-1198">Additional Information</h3>
                <dl className="space-y-4 text-sm" data-id="element-1199">
                  {formData.categories.length > 0 && <div data-id="element-1200">
                      <dt className="text-gray-500" data-id="element-1201">Categories</dt>
                      <dd className="mt-1 font-medium" data-id="element-1202">
                        {formData.categories.join(', ')}
                      </dd>
                    </div>}
                  <div data-id="element-1203">
                    <dt className="text-gray-500" data-id="element-1204">Created By</dt>
                    <dd className="mt-1 font-medium" data-id="element-1205">{formData.createdBy}</dd>
                  </div>
                  <div data-id="element-1206">
                    <dt className="text-gray-500" data-id="element-1207">AIs Used</dt>
                    <dd className="mt-1 font-medium" data-id="element-1208">
                      {formData.aisUsed.join(', ') || 'Not specified'}
                    </dd>
                  </div>
                  <div data-id="element-1209">
                    <dt className="text-gray-500" data-id="element-1210">Version</dt>
                    <dd className="mt-1 font-medium" data-id="element-1211">{formData.version}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-gray-50 min-h-screen pb-20" data-id="element-1212">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4 sticky top-0 z-10" data-id="element-1213">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 flex justify-between items-center" data-id="element-1214">
          <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-indigo-600 transition-colors" data-id="element-1215">
            <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-1216" />
            <span className="text-sm font-medium" data-id="element-1217">Back</span>
          </button>
          <button onClick={() => setIsPreviewMode(true)} className="flex items-center px-4 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors" data-id="element-1218">
            <EyeIcon className="h-4 w-4 mr-2" data-id="element-1219" />
            <span className="text-sm font-medium" data-id="element-1220">Preview</span>
          </button>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8" data-id="element-1221">
        <div className="mb-8" data-id="element-1222">
          <h1 className="text-3xl font-bold mb-2" data-id="element-1223">App Store Listing</h1>
          <p className="text-gray-600" data-id="element-1224">
            Customize how your app appears in the store
          </p>
        </div>
        <div className="space-y-6" data-id="element-1225">
          {/* Basic Information */}
          <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-1226">
            <h2 className="text-xl font-semibold mb-6" data-id="element-1227">Basic Information</h2>
            <div className="space-y-5" data-id="element-1228">
              <div data-id="element-1229">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1230">
                  App Name
                </label>
                <input type="text" value={formData.title} onChange={e => setFormData({
                ...formData,
                title: e.target.value
              })} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" placeholder="Enter app name" data-id="element-1231" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1232">
                <div data-id="element-1233">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1234">
                    Author Name
                  </label>
                  <input type="text" value={formData.author} onChange={e => setFormData({
                  ...formData,
                  author: e.target.value
                })} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" placeholder="Your name" data-id="element-1235" />
                </div>
                <div data-id="element-1236">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1237">
                    Company/Creator
                  </label>
                  <input type="text" value={formData.createdBy} onChange={e => setFormData({
                  ...formData,
                  createdBy: e.target.value
                })} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" placeholder="Your company name" data-id="element-1238" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1239">
                <div data-id="element-1240">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1241">
                    Categories
                  </label>
                  <div className="w-full border border-gray-300 rounded-lg px-4 py-2.5 bg-gray-50 min-h-[42px] flex items-center" data-id="element-1242">
                    {formData.categories.length > 0 ? <div className="flex flex-wrap gap-2" data-id="element-1243">
                        {formData.categories.map((category, index) => <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800" data-id="element-1244">
                            {category}
                          </span>)}
                      </div> : <span className="text-gray-400 text-sm" data-id="element-1245">
                        No categories selected
                      </span>}
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500" data-id="element-1246">
                    Selected from previous form
                  </p>
                </div>
                <div data-id="element-1247">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1248">
                    Price
                  </label>
                  <select value={formData.price} onChange={e => setFormData({
                  ...formData,
                  price: e.target.value
                })} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow bg-white" data-id="element-1249">
                    {priceOptions.map(option => <option key={option.value} value={option.value} data-id="element-1250">
                        {option.label}
                      </option>)}
                  </select>
                </div>
              </div>
              <div data-id="element-1251">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1252">
                  Version
                </label>
                <input type="text" value={formData.version} onChange={e => setFormData({
                ...formData,
                version: e.target.value
              })} className="w-full md:w-1/2 border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" placeholder="1.0.0" data-id="element-1253" />
              </div>
            </div>
          </section>
          {/* Screenshots */}
          <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-1254">
            <h2 className="text-xl font-semibold mb-2" data-id="element-1255">Screenshots</h2>
            <p className="text-sm text-gray-600 mb-6" data-id="element-1256">
              Upload images showing your app in action
            </p>
            <div className="space-y-4" data-id="element-1257">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1258">
                {formData.screenshots.map((screenshot, index) => <div key={index} className="relative group" data-id="element-1259">
                    <img src={screenshot} alt={`Screenshot ${index + 1}`} className="w-full aspect-video object-cover rounded-lg border-2 border-gray-200" data-id="element-1260" />
                    <button onClick={() => removeScreenshot(index)} className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" data-id="element-1261">
                      <XIcon className="h-4 w-4" data-id="element-1262" />
                    </button>
                  </div>)}
                <label className="flex flex-col items-center justify-center aspect-video border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 cursor-pointer transition-all" data-id="element-1263">
                  <UploadIcon className="h-8 w-8 text-gray-400 mb-2" data-id="element-1264" />
                  <span className="text-sm text-gray-600 font-medium" data-id="element-1265">
                    Add Screenshot
                  </span>
                  <input type="file" accept="image/*" multiple onChange={handleImageUpload} className="hidden" data-id="element-1266" />
                </label>
              </div>
              <p className="text-xs text-gray-500" data-id="element-1267">
                Recommended size: 1280x720px or 16:9 aspect ratio
              </p>
            </div>
          </section>
          {/* Description */}
          <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-1268">
            <h2 className="text-xl font-semibold mb-6" data-id="element-1269">Description</h2>
            <div className="space-y-5" data-id="element-1270">
              <div data-id="element-1271">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1272">
                  Short Description
                </label>
                <textarea value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} className="w-full border border-gray-300 rounded-lg px-4 py-3 h-24 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow resize-none" placeholder="Brief description of your app" data-id="element-1273" />
              </div>
              <div data-id="element-1274">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1275">
                  Detailed Description
                  <span className="text-gray-400 font-normal ml-1" data-id="element-1276">
                    (Optional)
                  </span>
                </label>
                <textarea value={formData.additionalDescription} onChange={e => setFormData({
                ...formData,
                additionalDescription: e.target.value
              })} className="w-full border border-gray-300 rounded-lg px-4 py-3 h-32 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow resize-none" placeholder="Provide more details about features, use cases, etc." data-id="element-1277" />
              </div>
            </div>
          </section>
          {/* Key Features */}
          <section className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-1278">
            <h2 className="text-xl font-semibold mb-2" data-id="element-1279">Key Features</h2>
            <p className="text-sm text-gray-600 mb-6" data-id="element-1280">
              Highlight the main features of your app
            </p>
            <div className="space-y-3" data-id="element-1281">
              {formData.features.map((feature, index) => <div key={index} className="flex items-center space-x-2" data-id="element-1282">
                  <input type="text" value={feature} onChange={e => updateFeature(index, e.target.value)} className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow" placeholder="Describe a key feature" data-id="element-1283" />
                  {formData.features.length > 1 && <button onClick={() => removeFeature(index)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" data-id="element-1284">
                      <XIcon className="h-5 w-5" data-id="element-1285" />
                    </button>}
                </div>)}
              <button onClick={addFeature} className="flex items-center text-indigo-600 hover:text-indigo-700 text-sm font-medium mt-2" data-id="element-1286">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-1287" />
                Add Feature
              </button>
            </div>
          </section>
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6" data-id="element-1288">
            <button onClick={() => navigate(-1)} className="px-6 py-2.5 border-2 border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors" data-id="element-1289">
              Cancel
            </button>
            <button onClick={() => setIsPreviewMode(true)} className="px-6 py-2.5 bg-white border-2 border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50 font-medium transition-colors flex items-center justify-center" data-id="element-1290">
              <EyeIcon className="h-4 w-4 mr-2" data-id="element-1291" />
              Preview
            </button>
            <button className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors flex items-center justify-center" data-id="element-1292">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-1293" />
              Save Draft
            </button>
            <button className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium transition-colors shadow-sm" data-id="element-1294">
              Publish to Store
            </button>
          </div>
        </div>
      </div>
    </div>;
};